<script setup>
// Renders a server-built `chart` attachment (line / bar / pie). Attachments
// are immutable, so the Chart.js instance is built once at mount — no watchers.
import { onBeforeUnmount, onMounted, ref } from 'vue'
import Chart from 'chart.js/auto'

const props = defineProps({
  attachment: { type: Object, required: true },
})

const canvas = ref(null)
let chart = null

const fmtTick = (ms) =>
  new Date(ms).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })

const tickFont = { size: 9 }
const mutedColor = 'rgba(115, 115, 115, 0.9)'
const gridColor = 'rgba(115, 115, 115, 0.12)'

function baseOptions(showLegend) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: showLegend
        ? { position: 'bottom', labels: { font: tickFont, color: mutedColor, boxWidth: 8, boxHeight: 8 } }
        : { display: false },
    },
  }
}

function valueScales(extraX = {}) {
  return {
    x: {
      grid: { display: false },
      ticks: { font: tickFont, color: mutedColor, maxTicksLimit: 6, maxRotation: 0 },
      ...extraX,
    },
    y: {
      beginAtZero: true,
      grid: { color: gridColor },
      ticks: { font: tickFont, color: mutedColor, maxTicksLimit: 4, precision: 0 },
    },
  }
}

function buildConfig() {
  const a = props.attachment
  const series = a.series || []

  if (a.kind === 'pie') {
    const points = series[0]?.points || []
    return {
      type: 'doughnut',
      data: {
        labels: points.map((p) => p.label),
        datasets: [{
          data: points.map((p) => p.y),
          backgroundColor: points.map((p, i) => p.color || series[0].color),
          borderWidth: 0,
        }],
      },
      options: { ...baseOptions(true), cutout: '55%' },
    }
  }

  if (a.kind === 'bar') {
    const points = series[0]?.points || []
    return {
      type: 'bar',
      data: {
        labels: points.map((p) => p.label),
        datasets: [{
          label: series[0]?.label,
          data: points.map((p) => p.y),
          backgroundColor: points.map((p) => p.color || series[0].color),
          borderRadius: 2,
        }],
      },
      options: { ...baseOptions(false), scales: valueScales() },
    }
  }

  return {
    type: 'line',
    data: {
      datasets: series.map((s) => ({
        label: s.label,
        data: (s.points || []).map((p) => ({ x: p.x, y: p.y })),
        borderColor: s.color,
        backgroundColor: s.color,
        borderWidth: 1.5,
        pointRadius: 0,
        tension: 0.3,
      })),
    },
    options: {
      ...baseOptions(series.length > 1),
      scales: valueScales({ type: 'linear', ticks: {
        font: tickFont, color: mutedColor, maxTicksLimit: 5, maxRotation: 0,
        callback: (value) => fmtTick(value),
      } }),
    },
  }
}

onMounted(() => {
  chart = new Chart(canvas.value, buildConfig())
})

onBeforeUnmount(() => {
  chart?.destroy()
  chart = null
})
</script>

<template>
  <canvas ref="canvas"></canvas>
</template>
